/**
 * Clamps translation to keep document centered and within bounds
 * For document viewer: when zoomed out, center the page (0,0)
 * When zoomed in, prevent panning beyond edges
 */
export function clampTranslation(
  translation: { x: number; y: number },
  scale: number,
  pageDimension: { width: number; height: number } | null
): { x: number; y: number } {
  // When at or below 100% zoom, always center the document
  if (scale <= 1) {
    return { x: 0, y: 0 };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // If no page dimension available, return as-is
  if (!pageDimension) {
    return translation;
  }

  const contentWidth = pageDimension.width * scale;
  const contentHeight = pageDimension.height * scale;

  // If content is smaller than viewport in any dimension, center it
  let clampedX = translation.x;
  let clampedY = translation.y;

  // Horizontal clamping
  if (contentWidth <= viewportWidth) {
    // Content fits in viewport horizontally - center it
    clampedX = 0;
  } else {
    // Content is larger than viewport - prevent over-panning
    // Max translation is 0 (left edge of content at left edge of viewport)
    // Min translation prevents right edge from showing empty space
    const minX = viewportWidth - contentWidth;

    clampedX = Math.max(minX, Math.min(0, translation.x));
  }

  // Vertical clamping
  if (contentHeight <= viewportHeight) {
    // Content fits in viewport vertically - center it
    clampedY = 0;
  } else {
    // Content is larger than viewport - prevent over-panning
    const minY = viewportHeight - contentHeight;

    clampedY = Math.max(minY, Math.min(0, translation.y));
  }

  return { x: clampedX, y: clampedY };
}

/**
 * Adjusts scroll position to keep the viewport center fixed during zoom
 * Used for keyboard shortcuts where there's no cursor position
 */
export function adjustScrollForCenteredZoom(currentZoom: number, newZoom: number): void {
  const currentScale = currentZoom / 100;
  const newScale = newZoom / 100;

  // Calculate viewport center position
  const viewportCenterX = window.innerWidth / 2;
  const viewportCenterY = window.innerHeight / 2;

  // Calculate viewport center in document coordinates
  const centerDocX = viewportCenterX + window.scrollX;
  const centerDocY = viewportCenterY + window.scrollY;

  // Calculate the point on the unscaled content under the viewport center
  const contentPointX = centerDocX / currentScale;
  const contentPointY = centerDocY / currentScale;

  // Calculate where that point will be after new zoom
  const newCenterDocX = contentPointX * newScale;
  const newCenterDocY = contentPointY * newScale;

  // Calculate new scroll position to keep viewport center over the same content point
  const newScrollX = newCenterDocX - viewportCenterX;
  const newScrollY = newCenterDocY - viewportCenterY;

  // Apply scroll before zoom to ensure they happen together
  document.documentElement.scrollLeft = newScrollX;
  document.documentElement.scrollTop = newScrollY;
}
